import { AnyAction, Dispatch } from 'redux'
import { AxiosError, AxiosResponse } from 'axios'
import {
  errorLogger,
  getErrorMessages,
  isAdminRole,
  LoadingTypes,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'

import {
  getListingDataById,
  getProductDataFromLink,
  putButtonTemplateData,
} from '../../../api/marketplace/index'
import {
  DefaultSuccesrOnSubmitData,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import type { AppDispatch, RootState } from '../../../store/types'
import { PartnerType } from '../../../types/external'
import {
  ButtonCreationActionTypes,
  type ButtonInCreation,
  type ButtonsData,
} from './types'
import { transformDataFromGet } from './utils/transformDataFromGet'

export { ButtonCreationActionTypes }
export { transformDataFromGet } from './utils/transformDataFromGet'

export const geButtonById =
  (integrationId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.BUTTON_STOREFRONT_LOADING))
    await dispatch(clearButtonData())
    try {
      const response = await getListingDataById(integrationId)
      const { data } = response
      const updatedData = transformDataFromGet(data as Record<string, unknown>)
      dispatch(setButtonFormData(updatedData))
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch details for the listing.',
      ])(e as AxiosResponse<ErrorResponse>)

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.BUTTON_STOREFRONT_LOADING))
    }
  }

export const saveButtonStyleData =
  (buttonData: ButtonInCreation) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { id, name, type, product, ...restData } = buttonData.buttonData
      const cards = product.map(prod => {
        const match = prod.url.match(/prodview-[a-zA-Z0-9]+/)
        return match ? match[0] : null
      })
      const updatedData = {
        id: id,
        data: {
          ...restData,
        },
        type: type,
        integration_data: {
          integration_id: getState().buttonCreation.buttonData.integrationId,
          cards: cards,
          type: type,
          name: name,
        },
      }
      const partnerId = getState().AccountData.user.partnerData?.partnerId || ''
      const { data } = (await putButtonTemplateData(
        partnerId,
        updatedData
      )) as {
        data: {
          button_id?: string
          integration_id?: string
          template_id?: string
        }
      }
      const getTemplateId = data?.button_id || null
      const getIntegrationId = data?.integration_id || null
      const updatedTemplateData = {
        ...buttonData.buttonData,
        id: getTemplateId,
        ...data,
        integrationId:
          getIntegrationId ||
          getState().buttonCreation.buttonData.integrationId,
      }
      await dispatch(setButtonFormData(updatedTemplateData))
      await dispatch(
        updateAppAlert({
          message: DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (data.template_id) {
        return data.template_id
      }
      if (data.integration_id) {
        return data.integration_id
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const fetchProductDetailsFromUrlButtons =
  (productId: string, index: number, buttonData: ButtonInCreation) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.PRODUCT_INFORMATION))
    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        state.AccountData?.[partnerType]?.partnerData?.partnerId || ''
      const response = await getProductDataFromLink(partnerId, productId)
      const { data } = response as {
        data: { product_data?: Record<string, unknown> }
      }
      buttonData.buttonData.product[index] = {
        ...buttonData.buttonData.product[index],
        product_id: productId,
        isLoading: false,
        data: { ...data.product_data },
      }
      dispatch(setButtonFormData(buttonData.buttonData))
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch details for the provided url.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
      console.warn(e)
    } finally {
      dispatch(stopLoading(LoadingTypes.PRODUCT_INFORMATION))
    }
  }

export const setButtonFormData = (integrationData: ButtonsData) => {
  return {
    type: ButtonCreationActionTypes.SET_CREATE_BUTTON_DATA,
    payload: integrationData,
  }
}
export const clearButtonData = () =>
  ({
    type: ButtonCreationActionTypes.CLEAR_CURRENT_BUTTON_DATA,
  }) as unknown as AnyAction
